import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Gift, Radio, Film, Coins, Users, ArrowLeft, Loader2 } from "lucide-react";

export default function MyGifts() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all"); // all | reel | live
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gifts/sent")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const items = (data?.items || []).filter(g => tab === "all" || g.source === tab);

  return (
    <div className="px-4 py-6 pb-24" data-testid="mygifts-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/profile" data-testid="mygifts-back" className="p-2 rounded-full bg-white/5 active:scale-90">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="font-outfit font-extrabold text-2xl tracking-tighter">My Gifts</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="mygifts-summary">
            <div className="rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-transparent border border-[#FF7A00]/30 p-3">
              <Gift className="w-4 h-4 text-[#FF7A00] mb-1"/>
              <div className="text-xl font-extrabold">{data?.summary?.total_gifts_sent ?? 0}</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Gifts Sent</div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#00E5FF]/15 to-transparent border border-[#00E5FF]/30 p-3">
              <Coins className="w-4 h-4 text-[#00E5FF] mb-1"/>
              <div className="text-xl font-extrabold">{(data?.summary?.total_goldly_spent ?? 0).toLocaleString()}</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Goldly Spent</div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#FF2A5F]/15 to-transparent border border-[#FF2A5F]/30 p-3">
              <Users className="w-4 h-4 text-[#FF2A5F] mb-1"/>
              <div className="text-xl font-extrabold">{data?.summary?.unique_recipients ?? 0}</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Creators</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-3" data-testid="mygifts-tabs">
            {[
              { id: "all", label: "All", icon: Gift },
              { id: "reel", label: "On Reels", icon: Film },
              { id: "live", label: "On Live", icon: Radio },
            ].map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`mygifts-tab-${t.id}`}
                  className={`flex-1 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 border ${active ? "border-[#FF7A00] bg-[#FF7A00]/15 text-[#FF7A00]" : "border-white/10 text-white/60"}`}>
                  <Icon className="w-3.5 h-3.5"/>{t.label}
                </button>
              );
            })}
          </div>

          {/* List */}
          {items.length === 0 ? (
            <div className="text-center py-12 text-white/50" data-testid="mygifts-empty">
              <Gift className="w-10 h-10 mx-auto mb-2 opacity-50"/>
              <p className="text-sm">No gifts sent yet.</p>
              <p className="text-[11px] mt-1">Send gifts to creators on reels or live streams to support them.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="mygifts-list">
              {items.map((g, idx) => (
                <div key={g.id || idx} data-testid={`mygift-${idx}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10">
                  <div className="text-3xl flex-shrink-0">{g.gift_emoji || "🎁"}</div>
                  <Link to={g.to_user_id ? `/u/${g.to_user_id}` : "#"} className="flex items-center gap-2 flex-1 min-w-0">
                    {g.to_avatar && <img src={g.to_avatar} alt="" className="w-9 h-9 rounded-full border border-white/15 flex-shrink-0"/>}
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate">
                        {g.gift_name} <span className="text-white/40 font-normal">→</span>{" "}
                        <span className="text-[#00E5FF]">@{g.to_username || "creator"}</span>
                      </div>
                      <div className="text-[10px] text-white/50 flex items-center gap-1.5 mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded-md ${g.source === "live" ? "bg-[#FF2A5F]/20 text-[#FF2A5F]" : "bg-[#00E5FF]/20 text-[#00E5FF]"}`}>
                          {g.source === "live" ? "Live" : "Reel"}
                        </span>
                        <span>{new Date(g.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                      </div>
                    </div>
                  </Link>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-extrabold text-[#FF7A00]">-{g.price}</div>
                    <div className="text-[9px] text-white/40 uppercase">Goldly</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
